import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';

import { ApCookieService } from '@ap/persistence';
import { SessiebeheerResource, Session } from '@ap/interface-apic-sessiebeheer';
import { Channel, NextBestAction, PegaCDHContainerResponseContainer, PegaResource } from '@ap/interface-pega';

import { ResultPageMapper } from './result-page.mapper';
import { NextBestActionResponse } from './result-page.model';

/**
 * @ngdoc service
 * @name ResultPageService
 * @module ResultPageModule
 *
 * @description
 * Service to manage NBA's.
 **/
@Injectable({
    providedIn: 'root'
})
export class ResultPageService {
    public static readonly consentCookieName = 'CLC_GDPR';
    public static readonly customerIdentificationCookieValue = 'deq_pers_digitalData_l';

    private session: Session;
    private customerId: string;
    private customerGuid: string;

    public constructor(
        private readonly cookieService: ApCookieService,
        private readonly pegaResource: PegaResource,
        private readonly sessiebeheerResource: SessiebeheerResource
    ) {}

    public getNextBestAction(pegaContainerName: string): Observable<NextBestAction> {
        const consentCookie = this.cookieService.get(ResultPageService.consentCookieName);
        return this.getCustomerIdentification().pipe(
            switchMap((customerId: string) =>
                this.pegaResource
                    .getNextBestAction(
                        customerId,
                        pegaContainerName,
                        [
                            {
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                Type: 'Cookie',
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                Key: 'CLC_GDPR',
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                Value: consentCookie
                            }
                        ],
                        Channel.WEB,
                        !!customerId ? undefined : this.customerGuid
                    )
                    .pipe(
                        catchError(() => {
                            return of(null);
                        })
                    )
            ),
            switchMap((containerResponse) => containerResponse.ContainerList ?? [{} as PegaCDHContainerResponseContainer]),
            map((responseContainer) => ResultPageMapper.mapRankedResultsToNextBestAction(responseContainer.RankedResults)),
            catchError(() => of(null))
        );
    }

    public respondToNextBestAction(nbaResponse: NextBestActionResponse, pegaContainerName: string): void {
        this.getCustomerIdentification()
            .pipe(
                switchMap((customerId: string) =>
                    this.pegaResource
                        .nextBestActionResponse(
                            nbaResponse.nba,
                            customerId,
                            pegaContainerName,
                            nbaResponse.outcome,
                            Channel.WEB,
                            !!customerId ? undefined : this.customerGuid
                        )
                        .pipe(
                            catchError(() => {
                                return of(null);
                            })
                        )
                )
            )
            .subscribe();
    }

    public setPayedCookie(betaalverzoek_id: string): void {
        this.cookieService.set(betaalverzoek_id, betaalverzoek_id, '30');
    }

    private getCustomerIdentification(): Observable<string> {
        if (this.customerId) {
            return of(this.customerId);
        }

        this.getCookieCustomerIdentification();

        return this.getSession().pipe(
            map((session) => (session?.user ? PegaResource.getCustomerID(session.bank, session.user.id) : undefined)),
            tap((customerId) => (this.customerId = customerId))
        );
    }

    private getCookieCustomerIdentification(): string {
        if (this.customerGuid) {
            return this.customerGuid;
        }
        const deqCookie = this.cookieService.get(ResultPageService.customerIdentificationCookieValue);
        if (!deqCookie) {
            return undefined;
        }

        const deqData = JSON.parse(decodeURIComponent(deqCookie))['.*']?.data;
        this.customerGuid = deqData?.userGUID;
        return this.customerGuid;
    }

    private getSession(): Observable<Session> {
        if (this.session) {
            return of(this.session);
        }

        return this.sessiebeheerResource.getSession('0').pipe(
            catchError(() => of(null)),
            tap((session) => (this.session = session))
        );
    }
}
